import classNames from 'classnames'
import { graphql } from 'gatsby'
import React, { FC } from 'react'
import { SanityPeoplePage, SanityPerson } from '../../types/generated/graphql-types'
import { DocumentHead } from '../components/DocumentHead'
import { Grid } from '../components/Grid'
import { Layout } from '../components/Layout'
import { PersonList } from '../components/PersonList'
import { RichTextArea } from '../components/PortableText/RichTextArea'
import { PersonBlockSerializer } from '../components/PortableText/serializers/blocks/PersonBlockSerializer'
import { Portrait } from '../components/Portrait'
import { TableList } from '../components/TableList/TableList'
import { useTranslations } from '../hooks/useTranslations'
import { mapLanguageVersions } from '../mapping/DocumentHeadMapper'
import { mapSanityPersonToPersonLinkProps } from '../mapping/PersonMapper'
import { HeadPropsBase, PagePropsBase } from '../types/PagePropsBase'
import { notEmpty } from '../utilities/filters'
import { validImage } from '../utilities/imageFilters'
import { Presets } from '../utilities/imagePreset'
import { getImageProps } from '../utilities/imagePropUtils'
import { getTeamDisplayName } from '../utilities/people'
import { SearchResultsDropdownFilterType } from '../utilities/searchResults'
import { IS_EQT_FOUNDATION, IS_EQT_FUNDS } from '../utilities/site'
import { isNullOrWhiteSpace } from '../utilities/string'

interface PersonResult {
  sanityPerson: SanityPerson
  allSanityPerson: {
    nodes: SanityPerson[]
  }
  sanityPeoplePage: SanityPeoplePage
}

const Person: FC<PagePropsBase<PersonResult>> = ({ data }) => {
  const translations = useTranslations()

  const { allSanityPerson, sanityPerson, sanityPeoplePage, ...pageBaseProps } = data

  const {
    _rawBody: body,
    advisor,
    businessLine,
    country,
    lineOfWork,
    office,
    path,
    portrait,
    team,
    title,
    workTitle,
  } = sanityPerson

  const Image = validImage(portrait) ? (
    <Portrait
      {...getImageProps(portrait, Presets.square)}
      className="mb-30
            sm:-ml-30 sm:mr-30
            md:mr-0
            lg:-ml-45 lg:mr-120 xl:mx-0"
      imgClassName="w-full"
    />
  ) : (
    <></>
  )

  const rows = [
    {
      heading: translations.businessLine,
      field: businessLine?.title ?? lineOfWork ?? '',
    },
    {
      heading: translations.workTitle,
      field: workTitle ?? '',
    },
    {
      heading: translations.office,
      field: office ?? '',
    },
  ]

  if (advisor) {
    rows.unshift({
      heading: translations.country,
      field: country ?? '',
    })
  }

  const personInformation = rows
    .filter(item => !isNullOrWhiteSpace(item?.field))
    .map(item => ({
      columns: [
        {
          text: item.heading ?? '',
        },
        {
          text: String(item?.field) ?? '',
        },
      ],
    }))

  const persons = allSanityPerson?.nodes?.filter(notEmpty) ?? []
  const showMultipleTeams = IS_EQT_FOUNDATION
  const teamLimit = showMultipleTeams ? undefined : 8

  return (
    <Layout {...pageBaseProps} currentPageUrl={path ?? ''}>
      <Grid additionalClassName="mb-60 sm:mb-90 lg:mb-120">
        <Grid
          additionalClassName={classNames(
            'col-span-full mb-30',
            'sm:mb-60',
            'md:mb-75',
            'xl:mb-60',
            'xxl:mb-75'
          )}
        >
          {!IS_EQT_FUNDS && (
            <span
              className={classNames(
                'col-span-full mb-20 text-primary mt-30',
                'tb:col-span-2 tb:row-start-1 tb:ml-30',
                'sm:col-start-2 sm:col-span-2 sm:ml-0',
                'md:col-start-1 md:mt-45',
                'lg:col-start-2 md:col-span-2',
                'xl:mt-60',
                'xxl:col-start-3 xxl:col-span-3 xxl:mb-30 xxl:mt-75'
              )}
            >
              {team?.map((team, index) => (
                <small
                  className="block font-hero-subtitle text-hero-subtitle"
                  key={index}
                >
                  {getTeamDisplayName(translations, team ?? '')}
                </small>
              ))}
            </span>
          )}

          <h1
            className={classNames(
              'col-span-full mb-10 font-hero-title text-hero-title text-secondary-dark',
              'tb:col-span-2 tb:row-start-2 tb:ml-30 tb:mb-20',
              'sm:col-start-2 sm:col-span-2 sm:ml-0',
              'md:col-start-1',
              'lg:col-start-2 lg:mb-30',
              'xl:mb-45',
              'xxl:col-start-3 xxl:col-span-4',
              {
                'mt-30 md:mt-45 xl:mt-60 xxl:mt-75': IS_EQT_FUNDS,
              }
            )}
          >
            {title}
          </h1>

          <h2
            className={classNames(
              'col-span-full mr-30 font-h3 text-h3 text-secondary-darker',
              'tb:col-span-2 tb:row-start-3 tb:ml-30 tb:-mr-30',
              'sm:col-start-2 sm:col-span-2 sm:ml-0',
              'md:col-start-1 md:mx-0',
              'lg:col-start-2 md:col-span-2',
              'xxl:col-start-3 xxl:col-span-3'
            )}
          >
            {workTitle}
          </h2>
        </Grid>

        <div
          className={classNames(
            'col-span-full flex flex-col mb-30',
            'tb:col-span-2 tb:ml-30 tb:-mr-30 tb:mb-0',
            'sm:col-start-2 sm:col-span-2 sm:ml-0',
            'md:col-start-1',
            'lg:col-start-2 lg:col-span-3 lg:mr-0',
            'xxl:col-start-3 xxl:col-span-4'
          )}
        >
          {Image}

          <TableList
            additionalClassName="lg:mr-60 xl:mr-0"
            columnDefinitions={[
              {
                fontWeight: 'font-light',
              },
              {
                fontWeight: 'font-medium',
              },
            ]}
            listItemTextSize="font-t text-t-300 xl:text-t-500"
            rowGapY="py-8 xl:pt-10 xl:pb-15"
            rows={personInformation}
          />
        </div>

        <div
          className={classNames(
            'col-span-full mr-30',
            'tb:col-span-2 tb:ml-30 tb:mr-0',
            'sm:col-start-4 sm:col-span-2',
            'md:ml-0',
            'lg:col-start-5 lg:-mr-30',
            'xl:col-start-6 xl:col-span-3 xl:mr-0',
            'xxl:col-start-8'
          )}
        >
          {body && (
            <RichTextArea components={{ block: PersonBlockSerializer }} value={body} />
          )}
        </div>
      </Grid>

      {!IS_EQT_FUNDS &&
        team?.map((team, index) => {
          const heading = `${translations.other} ${
            getTeamDisplayName(translations, team) ?? ''
          }`

          const teamMembers = persons
            .filter(person => person.team?.includes(team))
            .map(mapSanityPersonToPersonLinkProps)

          if (teamMembers.length === 0) {
            return <React.Fragment key={index} />
          }

          const allButtonUrl =
            teamLimit && teamMembers.length > teamLimit && sanityPeoplePage?.path
              ? `${sanityPeoplePage?.path}?${SearchResultsDropdownFilterType.Team}=${team}`
              : ''

          return (
            <Grid additionalClassName="mb-60 sm:mb-90 lg:mb-120" as="aside" key={index}>
              <h2
                className={classNames(
                  'font-h2 text-h2 text-primary',
                  'col-span-full mb-30',
                  'tb:pt-45 tb:border-t tb:border-neutral-lighter',
                  'sm:col-start-2 sm:col-span-4 sm:px-30 sm:-mx-30 sm:mb-60',
                  'md:col-span-full md:mx-0 md:px-0',
                  'lg:col-start-2 lg:col-span-6 lg:-mx-30 lg:px-30',
                  'xl:col-start-2 xl:col-span-8 xl:mx-0 xl:px-0 xl:mb-75',
                  'xxl:col-start-3 xxl:col-span-10'
                )}
              >
                {heading}
              </h2>

              <PersonList
                allButtonText={translations.seeAll ?? ''}
                allButtonUrl={allButtonUrl}
                enableMoreButton={showMultipleTeams}
                persons={teamMembers.slice(0, teamLimit)}
              />
            </Grid>
          )
        })}
    </Layout>
  )
}

export const Head = ({ data, pageContext }: HeadPropsBase<PersonResult>) => {
  const { sanityPerson, ...pageBaseProps } = data
  const {
    basePage,
    indexing,
    metaDescription,
    metaImage,
    metaTitle,
    path,
    portrait,
    title,
    workTitle,
  } = sanityPerson

  return (
    <DocumentHead
      basePage={basePage}
      description={metaDescription ?? workTitle}
      homePage={pageBaseProps.homePage}
      indexing={indexing}
      language={pageContext.language ?? undefined}
      languageVersions={mapLanguageVersions(pageContext)}
      ogImage={metaImage ?? portrait}
      path={path ?? undefined}
      title={metaTitle ?? title}
    />
  )
}

export const query = graphql`
  query PersonPageQuery(
    $pageId: String!
    $teamFilterRegex: String!
    $language: String
    $teamLimit: Int
  ) {
    sanityPerson(_id: { eq: $pageId }) {
      _rawBody
      basePage {
        ...BasePageFragment
      }
      ...PersonFragment
    }

    allSanityPerson(
      filter: {
        _id: { ne: $pageId }
        advisor: { ne: true }
        team: { regex: $teamFilterRegex }
        language: { eq: $language }
      }
      sort: { title: ASC }
      limit: $teamLimit
    ) {
      nodes {
        ...PersonFragment
      }
    }

    sanityPeoplePage(language: { eq: $language }) {
      path
    }

    ...PageBaseFragment
  }
`

export default Person
